import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { 
  AlertTriangle, 
  TrendingUp,
  Zap,
  RefreshCw,
  Filter,
  X,
  ChevronDown,
  Calendar,
  Search,
  Download,
  FileText
} from 'lucide-react';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { downloadStyledXlsx } from '../../../utils/exportStyledXlsx';
import { ticketService } from '../../../api/ticketService';
import { usePriorities } from '../../../hooks/usePriorities';
import { getCurrentUser } from '../../../api/authService';
import { extractErrorMessage } from '../../../utils/errorHandler';
import EscalatedTicketsTable from '../../../components/EscalatedTicketsTable';

const EscalatedTickets = () => {
  const [escalationStats, setEscalationStats] = useState({
    totalEscalated: 0,
    criticalEscalated: 0,
    highEscalated: 0,
    mediumEscalated: 0
  });
  const [loadingStats, setLoadingStats] = useState(false);
  const [teamAgents, setTeamAgents] = useState([]);
  const [agentFilter, setAgentFilter] = useState('all');
  const [isTeamLead, setIsTeamLead] = useState(null); // null = not determined yet
  const [roleDetermined, setRoleDetermined] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { data: priorities = [] } = usePriorities();
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const skipInitialTableRefreshRef = useRef(true);

  // Check if user is Team Lead
  useEffect(() => {
    const user = getCurrentUser();
    const isLead = user?.roleCode === 'TEAM_LEAD' || user?.role === 'team-lead';
    setIsTeamLead(isLead);
    setRoleDetermined(true);
    
    if (isLead) {
      loadTeamAgents();
    }
  }, []);

  const loadTeamAgents = async () => {
    try {
      const response = await ticketService.getTeamAgents();
      const responseData = response?.data;
      
      if (responseData?.success && responseData?.data) {
        const agents = Array.isArray(responseData.data) ? responseData.data : [];
        setTeamAgents(agents);
      } else {
        console.warn('Failed to load team agents:', responseData?.message);
        setTeamAgents([]);
      }
    } catch (err) {
      console.error('Error loading team agents:', err);
      setTeamAgents([]);
    }
  };

  // Fetch function for escalated tickets - use useCallback to ensure it uses latest isTeamLead value
  const fetchEscalatedTickets = React.useCallback(async (params = {}) => {
    // Build filter parameters (fetchAll skips page/size — backend returns full unpaged set)
    const fetchParams = params.fetchAll
      ? { fetchAll: true }
      : {
          page: params.page || 0,
          size: params.size || 10
        };
    
    // Add search parameter
    if (searchTerm && searchTerm.trim()) {
      fetchParams.search = searchTerm.trim();
    }
    
    // Add agentId filter if agent is selected
    if (agentFilter !== 'all') {
      fetchParams.agentId = parseInt(agentFilter);
    }
    
    // Add priority filter
    if (priorityFilter !== 'all' && priorityFilter) {
      const priorityObj = priorities.find(p => 
        (p.priorityCode || p.priorityName || '').toLowerCase() === priorityFilter.toLowerCase() ||
        p.priorityId?.toString() === priorityFilter
      );
      if (priorityObj?.priorityId) {
        fetchParams.priorityId = priorityObj.priorityId;
      }
    }
    
    // Add date filters - convert to ISO format for backend (LocalDateTime format: YYYY-MM-DDTHH:mm:ss)
    if (startDate) {
      // Convert date string to LocalDateTime format (YYYY-MM-DDTHH:mm:ss) without timezone
      const startDateTime = new Date(startDate);
      startDateTime.setHours(0, 0, 0, 0); // Set to start of day
      const year = startDateTime.getFullYear();
      const month = String(startDateTime.getMonth() + 1).padStart(2, '0');
      const day = String(startDateTime.getDate()).padStart(2, '0');
      fetchParams.startDate = `${year}-${month}-${day}T00:00:00`;
    }
    if (endDate) {
      // Convert date string to LocalDateTime format (YYYY-MM-DDTHH:mm:ss) without timezone
      const endDateTime = new Date(endDate);
      endDateTime.setHours(23, 59, 59, 999); // Set to end of day
      const year = endDateTime.getFullYear();
      const month = String(endDateTime.getMonth() + 1).padStart(2, '0');
      const day = String(endDateTime.getDate()).padStart(2, '0');
      fetchParams.endDate = `${year}-${month}-${day}T23:59:59`;
    }

    // Export: always use /escalated?fetchAll=true (role-aware, unpaged). List view keeps existing endpoints.
    if (params.fetchAll) {
      return await ticketService.getEscalatedTickets(fetchParams);
    }
    
    // Double-check role if not determined yet
    if (!roleDetermined) {
      const user = getCurrentUser();
      const isLead = user?.roleCode === 'TEAM_LEAD' || user?.role === 'team-lead';
      if (isLead) {
        return await ticketService.getTeamEscalatedTicketsList(fetchParams);
      } else {
        return await ticketService.getEscalatedTickets(fetchParams);
      }
    }
    
    // For Team Leads, use the new dedicated list endpoint
    if (isTeamLead) {
      return await ticketService.getTeamEscalatedTicketsList(fetchParams);
    } else {
      // For Agents, use the regular escalated endpoint
      return await ticketService.getEscalatedTickets(fetchParams);
    }
  }, [isTeamLead, agentFilter, roleDetermined, priorityFilter, startDate, endDate, priorities, searchTerm]);

  const loadStatistics = async () => {
    try {
      setLoadingStats(true);
      const statsParams = {};
      if (isTeamLead && agentFilter !== 'all') {
        statsParams.agentId = parseInt(agentFilter, 10);
      }
      const response = await ticketService.getEscalatedStatistics(statsParams);
      const responseData = response?.data || response;

      if (responseData?.success && responseData?.data) {
        const stats = responseData.data;
        setEscalationStats({
          totalEscalated: stats.totalEscalated || 0,
          criticalEscalated: stats.criticalEscalated || 0,
          highEscalated: stats.highEscalated || 0,
          mediumEscalated: stats.mediumEscalated || 0
        });
      } else {
        setEscalationStats({
          totalEscalated: 0,
          criticalEscalated: 0,
          highEscalated: 0,
          mediumEscalated: 0
        });
      }
    } catch (err) {
      console.error('Failed to load statistics:', err);
      setEscalationStats({
        totalEscalated: 0,
        criticalEscalated: 0,
        highEscalated: 0,
        mediumEscalated: 0
      });
    } finally {
      setLoadingStats(false);
    }
  };

  // Load statistics - use count API for Team Leads, fetch all for Agents
  useEffect(() => {
    // Don't load statistics until role is determined
    if (!roleDetermined) {
      return;
    }

    loadStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeamLead, roleDetermined, agentFilter]);

  // Reload statistics when refresh is triggered
  useEffect(() => {
    if (refreshKey > 0 && roleDetermined) {
      loadStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Trigger table refresh when filters change (priority, dates, agent) — not on initial mount
  // Note: searchTerm is handled by EscalatedTicketsTable's own effect with debouncing
  useEffect(() => {
    if (skipInitialTableRefreshRef.current) {
      skipInitialTableRefreshRef.current = false;
      return;
    }
    if (roleDetermined && priorities.length > 0) {
      setRefreshKey((prev) => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorityFilter, startDate, endDate, agentFilter]);

  // Calculate escalation statistics from tickets
  const calculateEscalationStats = (tickets) => {
    let criticalCount = 0;
    let highCount = 0;
    let mediumCount = 0;
    
    tickets.forEach(ticket => {
      // Count by priority - check priorityId or priority field
      let priority = 'medium';
      if (ticket.priorityId) {
        const priorityMap = { 1: 'low', 2: 'medium', 3: 'high', 4: 'critical' };
        priority = priorityMap[ticket.priorityId] || 'medium';
      } else if (ticket.priority) {
        priority = ticket.priority.toLowerCase();
      } else if (ticket.priorityName) {
        priority = ticket.priorityName.toLowerCase();
      }
      
      if (priority === 'critical') criticalCount++;
      else if (priority === 'high') highCount++;
      else if (priority === 'medium') mediumCount++;
    });
    
    // Update state with calculated statistics (preserve totalEscalated if already set)
    setEscalationStats(prev => ({
      totalEscalated: prev.totalEscalated || tickets.length,
      criticalEscalated: criticalCount,
      highEscalated: highCount,
      mediumEscalated: mediumCount
    }));
  };

  const formatDateTimeExport = (isoString) => {
    if (!isoString) return '-';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return '-';
    }
  };

  const fetchEscalatedForExport = async () => {
    const response = await fetchEscalatedTickets({ fetchAll: true });
    const content = response?.data?.data?.content || [];
    return (content || []).map((ticket) => {
      let priorityName = 'Medium';
      if (ticket.priorityId && priorities.length > 0) {
        const p = priorities.find(pr => pr.priorityId === ticket.priorityId || String(pr.priorityId) === String(ticket.priorityId));
        if (p) priorityName = p.priorityName || p.priorityCode || 'Medium';
      } else if (ticket.priority) priorityName = ticket.priority;
      return {
        ticketNumber: ticket.ticketNumber || ticket.ticketId || 'N/A',
        subject: ticket.subject || '',
        priorityName,
        status: (ticket.status || 'open').toLowerCase(),
        escalatedDate: ticket.escalatedAt || ticket.escalatedDate || ticket.updatedAt || ticket.createdAt
      };
    });
  };

  const handleExportExcel = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const rows = await fetchEscalatedForExport();
      if (rows.length === 0) {
        toast.error('No tickets to export');
        return;
      }
      const headers = ['S.NO', 'Ticket Number', 'Subject', 'Priority', 'Status', 'Escalated Date'];
      const excelRows = rows.map((r, i) => [
        i + 1, r.ticketNumber, r.subject, r.priorityName, r.status, r.escalatedDate || '',
      ]);
      downloadStyledXlsx({
        fileName: `escalated-tickets-${new Date().toISOString().slice(0, 10)}.xlsx`,
        sheetName: 'Escalated',
        headers,
        rows: excelRows,
        numberColumns: [0],
        textColumns: [1, 2, 3, 4],
        dateColumns: [5],
        centerColumns: [0, 3, 4, 5],
      });
      toast.success('Tickets exported as Excel');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const rows = await fetchEscalatedForExport();
      if (rows.length === 0) {
        toast.error('No tickets to export');
        return;
      }
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('Escalated Tickets', 14, 15);
      doc.setFontSize(10);
      const body = rows.map((r, i) => [
        i + 1, r.ticketNumber, (r.subject || '').substring(0, 35), r.priorityName, r.status, formatDateTimeExport(r.escalatedDate)
      ]);
      autoTable(doc, {
        startY: 22,
        head: [['S.NO', 'Ticket Number', 'Subject', 'Priority', 'Status', 'Escalated Date']],
        body,
        styles: { fontSize: 7 }
      });
      doc.save(`escalated-tickets-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Tickets exported as PDF');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Escalated Tickets</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage escalated tickets assigned to you
          </p>
        </div>
        <div className="flex gap-3 flex-nowrap">
          <Button 
            variant="outline" 
            className="flex items-center gap-2"
            onClick={() => {
              setRefreshKey(prev => prev + 1);
            }}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <div className="relative flex-shrink-0">
            <button
              type="button"
              onClick={() => setExportMenuOpen((v) => !v)}
              disabled={exporting}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md transition-colors disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              <span>{exporting ? 'Exporting…' : 'Export'}</span>
              <ChevronDown className="h-4 w-4" />
            </button>
            {exportMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" aria-hidden onClick={() => setExportMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 py-1 z-50 min-w-[140px] rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                    onClick={handleExportExcel}
                  >
                    <FileText className="h-4 w-4" />
                    Export Excel
                  </button>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                    onClick={handleExportPdf}
                  >
                    <FileText className="h-4 w-4" />
                    Export PDF
                  </button>
                </div>
              </>
            )}
          </div>
          <Button 
            variant="outline" 
            className="flex items-center gap-2"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="w-4 h-4" />
            Filters
          </Button>
        </div>
      </div>

      {/* Filters Section */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 flex-nowrap overflow-x-auto">
              <div className="relative flex-shrink min-w-[180px] max-w-[260px] w-full">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search tickets..."
                  value={searchTerm}
                  onChange={(e) => {
                    e.preventDefault();
                    setSearchTerm(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  className="pl-9 pr-3 py-2 w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              {/* Priority Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                  value={priorityFilter}
                  onChange={(e) => {
                    e.preventDefault();
                    setPriorityFilter(e.target.value);
                  }}
                  className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[140px]"
                >
                  <option value="all">All Priority</option>
                  {priorities.map((priority) => (
                    <option key={priority.priorityId} value={priority.priorityId}>
                      {priority.priorityName || priority.priorityCode}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Start Date */}
              <div className="relative flex-shrink-0">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    e.preventDefault();
                    setStartDate(e.target.value);
                  }}
                  className="pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-w-[160px]"
                  placeholder="Start Date"
                />
              </div>

              {/* End Date */}
              <div className="relative flex-shrink-0">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    e.preventDefault();
                    setEndDate(e.target.value);
                  }}
                  className="pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-w-[160px]"
                  placeholder="End Date"
                />
              </div>

              {/* Agent Filter (for Team Leads) */}
              {isTeamLead && (
                <div className="relative flex-shrink-0">
                  <select
                    value={agentFilter}
                    onChange={(e) => {
                      e.preventDefault();
                      setAgentFilter(e.target.value);
                    }}
                    className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[140px]"
                  >
                    <option value="all">All Agents</option>
                    {teamAgents.map((agent) => (
                      <option key={agent.userId || agent.id} value={agent.userId || agent.id}>
                        {agent.name || agent.email || `Agent ${agent.userId || agent.id}`}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    setSearchTerm('');
                    setPriorityFilter('all');
                    setStartDate('');
                    setEndDate('');
                    setAgentFilter('all');
                  }}
                  className="text-sm px-4 py-2 whitespace-nowrap"
                >
                  Clear All
                </Button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setShowFilters(false);
                  }}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Escalation Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Escalated</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{escalationStats.totalEscalated || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
                <Zap className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Critical</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{escalationStats.criticalEscalated || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                <TrendingUp className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">High Priority</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{escalationStats.highEscalated || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reusable Escalated Tickets Table - Only render when role is determined */}
      {roleDetermined && (
        <EscalatedTicketsTable
          fetchFunction={fetchEscalatedTickets}
          viewTicketRoute="/agent/tickets/:id"
          emptyStateMessage="No tickets are currently escalated"
          showFilters={showFilters}
          refreshTrigger={refreshKey}
          searchTerm={searchTerm}
          externalPriorities={priorities}
        />
      )}
    </div>
  );
};

export default EscalatedTickets;
